import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { List, useTable } from '@refinedev/antd';
import {
  Table,
  Card,
  Tag,
  Space,
  Input,
  Button,
  Badge,
  Modal,
  Empty,
  Avatar,
  Select,
  Segmented,
  Drawer,
  Form,
  Skeleton,
  message as antdMessage,
} from 'antd';
import {
  SearchOutlined,
  MessageOutlined,
  PhoneOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiClient } from '../../lib/api-client';
import { t } from '../../i18n';

interface CancelReasonRef {
  id: number;
  reasonUz: string;
  reasonRu: string;
  reasonEn: string;
}

interface StaffRef {
  id: number;
  fullName: string | null;
  username: string;
}

interface CancelledTrip {
  id: number;
  tripType: 'INTERCITY_TAXI' | 'INTERREGION_TAXI' | 'POSTAGE' | 'CARGO' | 'YOL_YOLAKAY' | 'SHAHARLAR_ARO';
  status: string;
  requestedAt: string;
  createdAt: string;
  acceptedAt?: string | null;
  canceledAt?: string | null;
  cancelledAt?: string | null;
  cancelActor: 'CLIENT' | 'DRIVER' | 'ADMIN' | 'SYSTEM' | string | null;
  cancelReason: string | null;
  cancelReasonRef: CancelReasonRef | null;
  cancelledByStaff: StaffRef | null;
  messageCount: number;
  pickupRegion: { nameUz: string } | null;
  pickupDistrict: { nameUz: string } | null;
  destinationRegion: { nameUz: string } | null;
  destinationDistrict: { nameUz: string } | null;
  pickupAddress?: string;
  destinationAddress?: string;
  client: { id: number; fullName: string; phone: string };
  driver: { id: number; fullName: string; phone: string } | null;
}

interface ChatMessage {
  id: number;
  senderType: 'CLIENT' | 'DRIVER' | 'SUPPORT' | 'SYSTEM';
  content: string;
  createdAt: string;
  sender: { id: number; fullName: string | null; phone: string; photoUrl: string | null } | null;
}

type TripDateRange = 'today' | 'yesterday' | 'thisWeek' | 'thisMonth' | 'thisYear' | 'allTime';

// Same date-range resolution used by TripList — keeps the two screens in sync.
const resolveRange = (range: TripDateRange): { fromDate?: string; toDate?: string } => {
  const now = new Date();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  switch (range) {
    case 'yesterday': {
      const start = new Date(today);
      start.setDate(start.getDate() - 1);
      const end = new Date(today);
      end.setMilliseconds(-1);
      return { fromDate: start.toISOString(), toDate: end.toISOString() };
    }
    case 'thisWeek': {
      const start = new Date(today);
      const day = (start.getDay() + 6) % 7;
      start.setDate(start.getDate() - day);
      return { fromDate: start.toISOString(), toDate: now.toISOString() };
    }
    case 'thisMonth': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { fromDate: start.toISOString(), toDate: now.toISOString() };
    }
    case 'thisYear': {
      const start = new Date(today.getFullYear(), 0, 1);
      return { fromDate: start.toISOString(), toDate: now.toISOString() };
    }
    case 'allTime':
      return {};
    case 'today':
    default:
      return { fromDate: today.toISOString(), toDate: now.toISOString() };
  }
};

const tripTypeLabels: Record<string, string> = {
  INTERCITY_TAXI: t('tripType.intercityTaxi'),
  INTERREGION_TAXI: t('tripType.interregionTaxi'),
  SHAHARLAR_ARO: t('tripType.shaharlarAro'),
  POSTAGE: t('tripType.postage'),
  CARGO: t('tripType.cargo'),
  YOL_YOLAKAY: t('tripType.yolYolakay'),
};

const tripTypeColors: Record<string, string> = {
  INTERCITY_TAXI: 'blue',
  INTERREGION_TAXI: 'purple',
  SHAHARLAR_ARO: 'magenta',
  POSTAGE: 'orange',
  CARGO: 'cyan',
  YOL_YOLAKAY: 'green',
};

const formatDuration = (startIso: string, endIso?: string | null): string => {
  if (!startIso) return '-';
  const start = new Date(startIso).getTime();
  const end = endIso ? new Date(endIso).getTime() : Date.now();
  const diff = Math.max(0, end - start);
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}${t('time.day')} ${hours}${t('time.hour')} ${minutes}${t('time.minute')}`;
  if (hours > 0) return `${hours}${t('time.hour')} ${minutes}${t('time.minute')}`;
  if (minutes > 0) return `${minutes}${t('time.minute')} ${seconds}${t('time.second')}`;
  return `${seconds}${t('time.second')}`;
};

const waitColor = (startIso: string, endIso?: string | null): string => {
  const end = endIso ? new Date(endIso).getTime() : Date.now();
  const minutes = (end - new Date(startIso).getTime()) / 60000;
  if (minutes < 3) return 'success';
  if (minutes < 10) return 'warning';
  return 'error';
};

const actorColor = (actor: string | null): string => {
  switch (actor) {
    case 'CLIENT':
      return 'orange';
    case 'DRIVER':
      return 'red';
    case 'ADMIN':
      return 'purple';
    case 'SYSTEM':
      return 'default';
    default:
      return 'default';
  }
};

const actorLabel = (actor: string | null): string => {
  switch (actor) {
    case 'CLIENT':
      return t('trips.cancelActor.client');
    case 'DRIVER':
      return t('trips.cancelActor.driver');
    case 'ADMIN':
      return t('trips.cancelActor.admin');
    case 'SYSTEM':
      return t('trips.cancelActor.system');
    default:
      return actor || '-';
  }
};

const cancelReasonLabel = (record: CancelledTrip): string => {
  if (record.cancelReasonRef) return record.cancelReasonRef.reasonUz;
  if (record.cancelReason) return record.cancelReason;
  return '-';
};

export const CancelledTripList = () => {
  const navigate = useNavigate();
  const [tripTypeFilter, setTripTypeFilter] = useState<string | undefined>(undefined);
  const [actorFilter, setActorFilter] = useState<string | undefined>(undefined);
  const [range, setRange] = useState<TripDateRange>('today');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [activeSearch, setActiveSearch] = useState<string>('');
  const [pickupRegionId, setPickupRegionId] = useState<number | undefined>();
  const [destinationRegionId, setDestinationRegionId] = useState<number | undefined>();
  const [regions, setRegions] = useState<{ id: number; nameUz: string }[]>([]);

  const [chatTrip, setChatTrip] = useState<CancelledTrip | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [loadingChat, setLoadingChat] = useState(false);

  useEffect(() => {
    apiClient
      .get<{ id: number; nameUz: string }[]>('/admin/regions')
      .then((res) => {
        const arr = Array.isArray(res.data)
          ? res.data
          : (res.data as { data?: { id: number; nameUz: string }[] })?.data || [];
        setRegions(arr);
      })
      .catch(() => setRegions([]));
  }, []);

  const dateFilters = useMemo(() => {
    const { fromDate, toDate } = resolveRange(range);
    const out: Array<{ field: string; operator: 'gte' | 'lte'; value: string }> = [];
    if (fromDate) out.push({ field: 'fromDate', operator: 'gte', value: fromDate });
    if (toDate) out.push({ field: 'toDate', operator: 'lte', value: toDate });
    return out;
  }, [range]);

  const { tableProps } = useTable<CancelledTrip>({
    resource: 'admin/trips',
    syncWithLocation: true,
    pagination: { pageSize: 20 },
    filters: {
      permanent: [
        { field: 'status', operator: 'eq' as const, value: 'CANCELLED' },
        ...(tripTypeFilter ? [{ field: 'tripType', operator: 'eq' as const, value: tripTypeFilter }] : []),
        ...(actorFilter ? [{ field: 'cancelActor', operator: 'eq' as const, value: actorFilter }] : []),
        ...(activeSearch ? [{ field: 'search', operator: 'contains' as const, value: activeSearch }] : []),
        ...(pickupRegionId ? [{ field: 'pickupRegionId', operator: 'eq' as const, value: pickupRegionId }] : []),
        ...(destinationRegionId ? [{ field: 'destinationRegionId', operator: 'eq' as const, value: destinationRegionId }] : []),
        ...dateFilters,
      ],
    },
    sorters: { initial: [{ field: 'requestedAt', order: 'desc' }] },
  });

  const openChat = async (trip: CancelledTrip) => {
    setChatTrip(trip);
    setChatMessages([]);
    setLoadingChat(true);
    try {
      const response = await apiClient.get('/admin/chat/messages', {
        params: { tripId: trip.id, tripType: trip.tripType, limit: 200 },
      });
      setChatMessages(response.data?.data || []);
    } catch {
      antdMessage.error(t('trips.chatLoadError'));
    } finally {
      setLoadingChat(false);
    }
  };

  const closeChat = () => {
    setChatTrip(null);
    setChatMessages([]);
  };

  const columns = [
    {
      title: t('common.id'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id: number, record: CancelledTrip) => (
        <a onClick={() => navigate(`/trips/${record.tripType}/${id}`)}>#{id}</a>
      ),
    },
    {
      title: t('table.tripType'),
      dataIndex: 'tripType',
      key: 'tripType',
      render: (type: string) => (
        <Tag color={tripTypeColors[type]}>{tripTypeLabels[type] || type}</Tag>
      ),
    },
    {
      title: t('cancelledTrips.waitTime'),
      key: 'waitTime',
      render: (_: unknown, record: CancelledTrip) => {
        const start = record.requestedAt || record.createdAt;
        const end = record.canceledAt || record.cancelledAt;
        return (
          <Tag icon={<ClockCircleOutlined />} color={waitColor(start, end)}>
            {formatDuration(start, end)}
          </Tag>
        );
      },
    },
    {
      title: t('trips.cancelledBy'),
      key: 'cancelActor',
      render: (_: unknown, record: CancelledTrip) => (
        <Space size={4} direction="vertical">
          <Tag icon={<CloseCircleOutlined />} color={actorColor(record.cancelActor)}>
            {actorLabel(record.cancelActor)}
          </Tag>
          {record.cancelActor === 'ADMIN' && record.cancelledByStaff && (
            <span style={{ fontSize: 11, color: '#7F8C8D' }}>
              {record.cancelledByStaff.fullName || record.cancelledByStaff.username}
            </span>
          )}
        </Space>
      ),
    },
    {
      title: t('trips.cancelReason'),
      key: 'cancelReason',
      render: (_: unknown, record: CancelledTrip) => (
        <span style={{ maxWidth: 240, display: 'inline-block' }}>
          {cancelReasonLabel(record)}
        </span>
      ),
    },
    {
      title: t('table.route'),
      key: 'route',
      width: 200,
      render: (_: unknown, record: CancelledTrip) => {
        const pickup = [record.pickupRegion?.nameUz, record.pickupDistrict?.nameUz]
          .filter(Boolean)
          .join(', ');
        const dest = [record.destinationRegion?.nameUz, record.destinationDistrict?.nameUz]
          .filter(Boolean)
          .join(', ');
        const pickupText = pickup || record.pickupAddress || '-';
        const destText = dest || record.destinationAddress || '-';
        return (
          <div style={{ fontSize: 12, maxWidth: 180 }}>
            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={pickupText}>
              <EnvironmentOutlined style={{ color: '#52c41a' }} /> {pickupText}
            </div>
            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={destText}>
              <EnvironmentOutlined style={{ color: '#f5222d' }} /> {destText}
            </div>
          </div>
        );
      },
    },
    {
      title: t('table.client'),
      key: 'client',
      render: (_: unknown, record: CancelledTrip) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.client?.fullName || t('common.noName')}</div>
          <a
            href={`tel:${record.client?.phone}`}
            style={{ fontSize: 12, color: '#7F8C8D' }}
            onClick={(e) => e.stopPropagation()}
          >
            <PhoneOutlined /> {record.client?.phone}
          </a>
        </div>
      ),
    },
    {
      title: t('table.driver'),
      key: 'driver',
      render: (_: unknown, record: CancelledTrip) =>
        record.driver ? (
          <div>
            <div style={{ fontWeight: 500 }}>{record.driver.fullName || t('common.noName')}</div>
            <a
              href={`tel:${record.driver.phone}`}
              style={{ fontSize: 12, color: '#7F8C8D' }}
              onClick={(e) => e.stopPropagation()}
            >
              <PhoneOutlined /> {record.driver.phone}
            </a>
          </div>
        ) : (
          <span style={{ color: '#7F8C8D' }}>-</span>
        ),
    },
    {
      title: t('table.date'),
      key: 'canceledAt',
      render: (_: unknown, record: CancelledTrip) => {
        const d = record.canceledAt || record.cancelledAt || record.requestedAt;
        return d ? dayjs(d).format('DD.MM.YYYY HH:mm') : '-';
      },
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 80,
      render: (_: unknown, record: CancelledTrip) => (
        <Badge count={record.messageCount || 0} offset={[-4, 4]} size="small">
          <Button
            type="text"
            icon={<MessageOutlined />}
            onClick={() => openChat(record)}
            disabled={record.messageCount === 0}
            title={t('trips.chatMessages')}
          />
        </Badge>
      ),
    },
  ];

  return (
    <List
      title={t('cancelledTrips.title')}
      headerButtons={
        <Space wrap>
          <Segmented<TripDateRange>
            value={range}
            onChange={(val) => setRange(val as TripDateRange)}
            options={[
              { label: t('dashboard.range.today'), value: 'today' },
              { label: t('dashboard.range.yesterday'), value: 'yesterday' },
              { label: t('dashboard.range.thisWeek'), value: 'thisWeek' },
              { label: t('dashboard.range.thisMonth'), value: 'thisMonth' },
              { label: t('dashboard.range.thisYear'), value: 'thisYear' },
              { label: t('dashboard.range.allTime'), value: 'allTime' },
            ]}
          />
          <Select
            placeholder={t('common.type')}
            allowClear
            style={{ width: 140 }}
            value={tripTypeFilter}
            onChange={(value) => setTripTypeFilter(value)}
            options={Object.entries(tripTypeLabels).map(([value, label]) => ({
              value,
              label,
            }))}
          />
          <Select
            placeholder={t('trips.cancelledBy')}
            allowClear
            style={{ width: 160 }}
            value={actorFilter}
            onChange={(value) => setActorFilter(value)}
            options={[
              { value: 'CLIENT', label: t('trips.cancelActor.client') },
              { value: 'DRIVER', label: t('trips.cancelActor.driver') },
              { value: 'ADMIN', label: t('trips.cancelActor.admin') },
              { value: 'SYSTEM', label: t('trips.cancelActor.system') },
            ]}
          />
          <Select
            showSearch
            allowClear
            placeholder="Qayerdan (viloyat)"
            style={{ width: 180 }}
            value={pickupRegionId}
            onChange={(v) => setPickupRegionId(v)}
            optionFilterProp="label"
            options={regions.map((r) => ({ value: r.id, label: r.nameUz }))}
          />
          <Select
            showSearch
            allowClear
            placeholder="Qayerga (viloyat)"
            style={{ width: 180 }}
            value={destinationRegionId}
            onChange={(v) => setDestinationRegionId(v)}
            optionFilterProp="label"
            options={regions.map((r) => ({ value: r.id, label: r.nameUz }))}
          />
          <Button
            icon={<FilterOutlined />}
            onClick={() => {
              setSearchTerm(activeSearch);
              setFiltersOpen(true);
            }}
          >
            Filtrlar
            {activeSearch ? (
              <Tag color="blue" style={{ marginLeft: 6, marginRight: 0 }}>
                1
              </Tag>
            ) : null}
          </Button>
        </Space>
      }
    >
      <Table
        {...tableProps}
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        columns={columns}
        rowKey="id"
        scroll={{ x: 1200 }}
        loading={
          tableProps.loading
            ? { indicator: <Card bordered={false}><Skeleton active paragraph={{ rows: 8 }} /></Card> }
            : false
        }
      />

      <Drawer
        title="Qo'shimcha filtrlar"
        placement="right"
        width={360}
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        footer={
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Button
              onClick={() => {
                setSearchTerm('');
                setActiveSearch('');
                setFiltersOpen(false);
              }}
            >
              Tozalash
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setActiveSearch(searchTerm.trim());
                setFiltersOpen(false);
              }}
            >
              Qo'llash
            </Button>
          </Space>
        }
      >
        <Form layout="vertical">
          <Form.Item
            label="Qidiruv"
            help="Mijoz yoki haydovchining ismi yoki telefoni bo'yicha qidiring."
          >
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Masalan: 99890… yoki Aliyev"
              allowClear
              onPressEnter={() => {
                setActiveSearch(searchTerm.trim());
                setFiltersOpen(false);
              }}
            />
          </Form.Item>
        </Form>
      </Drawer>

      <Modal
        title={
          <Space>
            <MessageOutlined />
            <span>
              {t('trips.chatMessages')} — #{chatTrip?.id}
            </span>
          </Space>
        }
        open={!!chatTrip}
        onCancel={closeChat}
        footer={null}
        width={640}
      >
        {loadingChat ? (
          <Empty description={t('common.loading')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : chatMessages.length === 0 ? (
          <Empty description={t('trips.noChatMessages')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <div
            style={{
              maxHeight: 480,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              padding: 4,
            }}
          >
            {chatMessages.map((msg) => {
              const isClient = msg.senderType === 'CLIENT';
              const bg = isClient ? '#e6f7ff' : '#f6ffed';
              const border = isClient ? '#91d5ff' : '#b7eb8f';
              return (
                <div
                  key={msg.id}
                  style={{
                    display: 'flex',
                    flexDirection: isClient ? 'row' : 'row-reverse',
                    gap: 8,
                    alignItems: 'flex-start',
                  }}
                >
                  <Avatar size={32} src={msg.sender?.photoUrl}>
                    {msg.sender?.fullName?.[0]?.toUpperCase() || '?'}
                  </Avatar>
                  <div
                    style={{
                      maxWidth: '70%',
                      padding: '8px 12px',
                      borderRadius: 12,
                      backgroundColor: bg,
                      border: `1px solid ${border}`,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
                      <strong style={{ fontSize: 12 }}>
                        {msg.sender?.fullName || actorLabel(msg.senderType)}
                      </strong>
                      <span style={{ fontSize: 11, color: '#8c8c8c', whiteSpace: 'nowrap' }}>
                        {dayjs(msg.createdAt).format('DD.MM HH:mm')}
                      </span>
                    </div>
                    <div style={{ fontSize: 14, wordBreak: 'break-word' }}>{msg.content}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>
    </List>
  );
};
